import React, { useState } from "react";
import MySkeleton from "components/ui/feedback/Skeleton";
import { useStatsFetch } from "./useStatsApi";
import {
  StatCard,
  SectionCard,
  RefreshButton,
  ErrorMessage,
  SimpleTable,
  RateBar,
  Badge,
  formatNumber,
} from "./StatComponents";

const TOKEN_COLUMN_HELP =
  "Eski mobil kimlik doğrulama sistemindeki kayıt (Token koleksiyonu). Yeni sistem Mobile tablosunu kullanır; bu sütun geçiş dönemini izlemek içindir.";

const USER_TYPE_COLUMNS = [
  { key: "parents", label: "Veli" },
  { key: "personel", label: "Personel" },
  { key: "drivers", label: "Sürücü" },
  { key: "admins", label: "Admin" },
  { key: "other", label: "Diğer" },
];

const userTypeTableColumns = USER_TYPE_COLUMNS.map(({ key, label }) => ({
  key,
  label,
  align: "right",
  render: (row) => formatNumber(row.userTypes?.[key]),
}));

const UserTypeStatCards = ({ userTypes }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
    {USER_TYPE_COLUMNS.map(({ key, label }) => (
      <StatCard key={key} label={label} value={userTypes?.[key]} color="#546e7a" />
    ))}
  </div>
);

const formatVersionUserBreakdown = (userTypes) => {
  if (!userTypes) return null;
  const parts = USER_TYPE_COLUMNS.filter(({ key }) => userTypes[key] > 0).map(
    ({ key, label }) => `${formatNumber(userTypes[key])} ${label.toLowerCase()}`
  );
  return parts.length > 0 ? parts.join(", ") : null;
};

const formatVersionPlatformBreakdown = (row) => {
  const parts = [`${formatNumber(row.android || 0)} Android`, `${formatNumber(row.ios || 0)} iOS`];
  if ((row.unknownPlatform || 0) > 0) {
    parts.push(`${formatNumber(row.unknownPlatform)} bilinmeyen`);
  }
  return parts.join(", ");
};

const versionColumns = (withMobileCount) => [
  {
    key: "version",
    label: "Sürüm",
    render: (row) => (
      <>
        {row.version === "unknown" ? "Bilinmeyen" : row.version}{" "}
        <span style={{ fontWeight: 400, color: "#555" }}>({formatVersionPlatformBreakdown(row)})</span>{" "}
        {row.isLatest && (
          <Badge color="#c8e6c9" textColor="#1b5e20">
            Güncel
          </Badge>
        )}
      </>
    ),
  },
  {
    key: "users",
    label: "Kişi",
    align: "right",
    render: (row) => {
      const breakdown = formatVersionUserBreakdown(row.userTypes);
      return (
        <span>
          {formatNumber(row.users)}
          {breakdown && (
            <span style={{ display: "block", fontSize: "12px", color: "#666", fontWeight: 400 }}>({breakdown})</span>
          )}
        </span>
      );
    },
  },
  {
    key: "rate",
    label: "Oran",
    align: "right",
    render: (row) => (withMobileCount > 0 ? ((row.users / withMobileCount) * 100).toFixed(1) + "%" : "-"),
  },
];

const AppVersionTable = ({ versions, withMobileCount, emptyText = "Mobil kaydı olan kullanıcı yok." }) => (
  <SimpleTable
    columns={versionColumns(withMobileCount)}
    rows={versions}
    emptyText={emptyText}
    maxHeight="320px"
    rowKey={(row) => row.version}
  />
);

const MobileAdoptionSection = () => {
  const { data, isLoading, error, load } = useStatsFetch("/admin/statistics/mobile-adoption");
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);

  const totals = data?.totals;
  const companies = data?.companies || [];

  const schoolColumns = [
    { key: "school", label: "Okul" },
    { key: "totalUsers", label: "Kişi", align: "right", render: (row) => formatNumber(row.totalUsers) },
    ...userTypeTableColumns,
    { key: "withPhone", label: "Telefonlu", align: "right", render: (row) => formatNumber(row.withPhone) },
    { key: "withMobile", label: "Mobil", align: "right", render: (row) => formatNumber(row.withMobile) },
    { key: "withToken", label: "Token (Eski)", align: "right", render: (row) => formatNumber(row.withToken) },
    {
      key: "eitherRate",
      label: "Mobil veya Token",
      render: (row) => <RateBar rate={row.totalUsers > 0 ? (row.withEither / row.totalUsers) * 100 : 0} />,
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "15px" }}>
        <RefreshButton onClick={() => load()} isLoading={isLoading} />
      </div>

      <ErrorMessage message={error} />

      {isLoading && !data ? (
        <MySkeleton lineCount={10} />
      ) : data ? (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <StatCard label="Toplam Kişi" value={totals?.users} color="#1976d2" />
            <StatCard
              label="Telefonu Olan"
              value={totals?.withPhone}
              sub={totals?.users ? ((totals.withPhone / totals.users) * 100).toFixed(1) + "% kişi" : null}
              color="#0288d1"
            />
            <StatCard
              label="Mobil Kaydı Olan"
              value={totals?.withMobile}
              sub={totals?.withPhone ? ((totals.withMobile / totals.withPhone) * 100).toFixed(1) + "% telefonlu" : null}
              color="#2e7d32"
            />
            <StatCard
              label="Token Kaydı Olan (Eski)"
              value={totals?.withToken}
              sub={totals?.users ? ((totals.withToken / totals.users) * 100).toFixed(1) + "% kişi" : null}
              color="#f9a825"
            />
            <StatCard
              label="Mobil veya Token"
              value={totals?.withEither}
              sub={totals?.users ? ((totals.withEither / totals.users) * 100).toFixed(1) + "% kişi" : null}
              color="#7b1fa2"
            />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            {USER_TYPE_COLUMNS.map(({ key, label }) => (
              <StatCard key={key} label={label} value={totals?.userTypes?.[key]} color="#546e7a" />
            ))}
          </div>

          <SectionCard
            title={
              totals?.latestVersion
                ? `Uygulama Sürüm Dağılımı (Görülen son sürüm: ${totals.latestVersion})`
                : "Uygulama Sürüm Dağılımı"
            }
          >
            <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#666" }}>
              Mobil kaydı olan kullanıcıların hangi uygulama sürümünü kullandığı. Telefon numarası üzerinden Mobile
              tablosuyla eşleştirilir.
            </p>
            <AppVersionTable versions={totals?.appVersions} withMobileCount={totals?.withMobile || 0} />
          </SectionCard>

          <SectionCard title={`Şirket Bazında Mobil Kullanım (${companies.length} aktif şirket)`}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ background: "#f5f5f5" }}>
                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>Şirket</th>
                    <th style={{ padding: "10px", textAlign: "right", borderBottom: "2px solid #ddd" }}>Kişi</th>
                    {USER_TYPE_COLUMNS.map(({ key, label }) => (
                      <th
                        key={key}
                        style={{ padding: "10px", textAlign: "right", borderBottom: "2px solid #ddd", whiteSpace: "nowrap" }}
                      >
                        {label}
                      </th>
                    ))}
                    <th style={{ padding: "10px", textAlign: "right", borderBottom: "2px solid #ddd" }}>Telefonlu</th>
                    <th style={{ padding: "10px", textAlign: "right", borderBottom: "2px solid #ddd" }}>Mobil</th>
                    <th
                      style={{ padding: "10px", textAlign: "right", borderBottom: "2px solid #ddd" }}
                      title={TOKEN_COLUMN_HELP}
                    >
                      Token (Eski)
                    </th>
                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd", minWidth: "160px" }}>
                      Mobil veya Token
                    </th>
                    <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>Okullar</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => {
                    const isExpanded = expandedCompanyId === company.companyId;
                    return (
                      <React.Fragment key={company.companyId}>
                        <tr style={{ background: isExpanded ? "#e3f2fd" : "transparent" }}>
                          <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee", fontWeight: 500 }}>
                            {company.company}
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                            {formatNumber(company.totalUsers)}
                          </td>
                          {USER_TYPE_COLUMNS.map(({ key }) => (
                            <td
                              key={key}
                              style={{ padding: "8px 10px", borderBottom: "1px solid #eee", textAlign: "right" }}
                            >
                              {formatNumber(company.userTypes?.[key])}
                            </td>
                          ))}
                          <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                            {formatNumber(company.withPhone)}
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                            {formatNumber(company.withMobile)}
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                            {formatNumber(company.withToken)}
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee" }}>
                            <RateBar
                              rate={company.totalUsers > 0 ? (company.withEither / company.totalUsers) * 100 : 0}
                            />
                          </td>
                          <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee", textAlign: "center" }}>
                            <button
                              onClick={() => setExpandedCompanyId(isExpanded ? null : company.companyId)}
                              style={{
                                background: isExpanded ? "#1976d2" : "none",
                                color: isExpanded ? "#fff" : "#333",
                                border: "1px solid #ccc",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              {isExpanded ? "Kapat" : `Göster (${company.schools?.length || 0})`}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: "#e3f2fd" }}>
                            <td colSpan={7 + USER_TYPE_COLUMNS.length} style={{ padding: "12px 15px", borderBottom: "1px solid #eee" }}>
                              <div style={{ background: "#fff", padding: "12px", borderRadius: "6px" }}>
                                <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>Kullanıcı Tipi Dağılımı</h4>
                                <UserTypeStatCards userTypes={company.userTypes} />
                                {company.appVersions?.length > 0 && (
                                  <div style={{ marginBottom: "16px" }}>
                                    <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>
                                      {company.company} — Sürüm Dağılımı
                                    </h4>
                                    <AppVersionTable
                                      versions={company.appVersions}
                                      withMobileCount={company.withMobile}
                                    />
                                  </div>
                                )}
                                <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>Okul Kırılımı</h4>
                                <SimpleTable
                                  columns={schoolColumns}
                                  rows={company.schools}
                                  emptyText="Okul kırılımı yok."
                                  maxHeight="400px"
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </>
      ) : null}
    </div>
  );
};

export default MobileAdoptionSection;
